import React, { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOrder } from "@/hooks/useOrder";
import { useProducts } from "@/hooks/useProducts";
import { useProductMutations } from "@/hooks/useProductMutations";
import { FastTrackProductEntry } from "@/components/FastTrackProductEntry";

import { useToast } from "@/hooks/use-toast";
import { TestUploadButton } from "@/components/TestUploadButton";

const ManageOrderV2: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: order } = useOrder(orderId);
  const { data: products = [], isLoading } = useProducts(orderId || "");
  const { addProduct, deleteProduct } = useProductMutations(orderId || "");
  const [fastOpen, setFastOpen] = useState(false);

  const stats = useMemo(() => {
    const total = products.length;
    const committed = products.filter(p => p.submit_status === "committed").length;
    const pending = total - committed;
    return { total, committed, pending };
  }, [products]);

  const pageTitle = `Manage Order${order?.supplierName ? ` | ${order.supplierName}` : ""}`.slice(0, 60);
  const canonical = `${window.location.origin}/orders/${orderId}/manage`;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`Manage order ${orderId}. Add products, photos, and review status.`} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <header className="border-b bg-background/80 backdrop-blur">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/orders")}> 
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="text-xl font-semibold">Manage Order</h1>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <span>Order: <strong>{orderId}</strong></span>
            {order?.supplierName && <span>• Supplier: <strong>{order.supplierName}</strong></span>}
            {order?.deliveryDate && <span>• Delivery: <strong>{order.deliveryDate}</strong></span>}
            {order?.status && <span>• Status: <strong>{order.status}</strong></span>}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4">
        {/* Status Bar */}
        <section className="mb-4">
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <CardContent className="py-3"><div className="text-sm">Total</div><div className="text-2xl font-semibold">{stats.total}</div></CardContent>
            </Card>
            <Card>
              <CardContent className="py-3"><div className="text-sm">Committed</div><div className="text-2xl font-semibold">{stats.committed}</div></CardContent>
            </Card>
            <Card>
              <CardContent className="py-3"><div className="text-sm">Pending</div><div className="text-2xl font-semibold">{stats.pending}</div></CardContent>
            </Card>
          </div>
        </section>

        {/* Actions */}
        <section className="mb-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setFastOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Fast Add Product
            </Button>
            <Link to={`/orders/${orderId}/edit`} className="text-sm underline">Open legacy editor</Link>
            <TestUploadButton productId={orderId || "diagnostics"} />
          </div>
        </section>


        {/* Products List */}
        <section>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading products…</div>
            ) : products.length === 0 ? (
              <Card>
                <CardContent className="py-6 text-center text-sm text-muted-foreground">No products yet. Use Fast Add to start.</CardContent>
              </Card>
            ) : (
              products.map((p) => (
                <Card key={p.id}>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base truncate" title={p.name}>{p.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="aspect-video bg-muted rounded overflow-hidden">
                      {p.photos?.[0] ? (
                        <img src={p.photos[0]} alt={`${p.name} product photo`} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">No photo</div>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.barcode}</div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs">Status: <strong>{p.submit_status || 'pending'}</strong></div>
                      <Button variant="destructive" size="sm" onClick={async () => {
                        try {
                          await deleteProduct.mutateAsync(p.id);
                          toast({ title: "Product removed" });
                        } catch (e: any) {
                          toast({ title: "Delete failed", description: e.message, variant: "destructive" });
                        }
                      }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </section>
      </main>

      {/* Fast Add Popup (unchanged component) */}
      <FastTrackProductEntry
        isOpen={fastOpen}
        onClose={() => setFastOpen(false)}
        onAddProduct={async (product) => {
          try {
            await addProduct.mutateAsync({
              orderId: orderId!,
              barcode: product.barcode,
              name: product.name,
              boxes: product.boxes,
              unitsPerBox: product.unitsPerBox,
              photos: product.photos,
              creationSource: product.creationSource,
            });
            return true;
          } catch (e: any) {
            toast({ title: "Add failed", description: e.message, variant: "destructive" });
            return false;
          }
        }}
      />
    </div>
  );
};

export default ManageOrderV2;
